import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import LogoutIcon from '@mui/icons-material/Logout';
import MetaData from '../Layouts/MetaData';
import Loader from '../Layouts/Loader';

const VendorDashboard = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [vendor, setVendor] = useState(null);
    const [allocations, setAllocations] = useState([]);
    const [sales, setSales] = useState([]);
    const [offers, setOffers] = useState([]);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const meRes = await axios.get('/api/v1/vendor/me');
                setVendor(meRes.data.vendor);

                const [allocRes, salesRes, offersRes] = await Promise.all([
                    axios.get('/api/v1/vendor/my-allocations'),
                    axios.get('/api/v1/vendor/my-sales'),
                    axios.get('/api/v1/vendor/my-offers'),
                ]);
                setAllocations(allocRes.data.allocations);
                setSales(salesRes.data.sales);
                setOffers(offersRes.data.offers);
            } catch (error) {
                // Not logged in, or session expired — back to login.
                navigate('/vendor/login');
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const logoutHandler = async () => {
        try {
            await axios.get('/api/v1/vendor/logout');
        } catch (error) { /* ignore */ }
        navigate('/vendor/login');
    };

    if (loading) return <Loader />;
    if (!vendor) return null;

    // Group allocations by product, showing each store's remaining qty side by side.
    const byProduct = {};
    allocations.forEach((a) => {
        const key = a.product?._id;
        if (!byProduct[key]) byProduct[key] = { product: a.product, stores: [] };
        byProduct[key].stores.push({ store: a.store, quantity: a.quantity });
    });

    const totalRemaining = allocations.reduce((sum, a) => sum + a.quantity, 0);
    const totalSold = sales.reduce((sum, s) => sum + s.quantitySold, 0);

    return (
        <div className="min-h-screen bg-gray-50 pb-10">
            <MetaData title="Vendor Portal | 1313 Mart" />

            <div className="bg-white shadow px-6 py-4 flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-lg font-bold text-primary-orange">1313 Mart Vendor Portal</h1>
                    <p className="text-sm text-gray-500">{vendor.name}{vendor.brandName && ` — ${vendor.brandName}`}</p>
                </div>
                <button onClick={logoutHandler} className="flex items-center gap-1 text-sm text-gray-600 hover:text-red-600">
                    <LogoutIcon sx={{ fontSize: "18px" }} /> Log Out
                </button>
            </div>

            <div className="max-w-5xl mx-auto px-4 flex flex-col gap-4">

                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 bg-white shadow rounded-lg p-5">
                        <p className="text-xs text-gray-400 uppercase">Total Remaining Stock</p>
                        <p className="text-2xl font-semibold text-primary-orange">{totalRemaining.toLocaleString()} units</p>
                    </div>
                    <div className="flex-1 bg-white shadow rounded-lg p-5">
                        <p className="text-xs text-gray-400 uppercase">Total Units Sold</p>
                        <p className="text-2xl font-semibold text-primary-green">{totalSold.toLocaleString()} units</p>
                    </div>
                </div>

                {offers.length > 0 && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-5">
                        <h3 className="font-medium text-gray-800 mb-2">🎉 Active Offers on Your Products</h3>
                        <div className="flex flex-col gap-1">
                            {offers.map((offer) => (
                                <p key={offer._id} className="text-sm text-gray-700">
                                    <span className="font-medium">{offer.name}</span> — applies to: {offer.products.map((p) => p.name).join(', ')}
                                </p>
                            ))}
                        </div>
                    </div>
                )}

                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <h3 className="font-medium text-lg p-6 pb-0">Your Inventory by Store</h3>
                    <table className="w-full text-sm mt-3">
                        <thead>
                            <tr className="text-left text-gray-500 text-xs uppercase border-b">
                                <th className="px-6 py-2">Product</th>
                                <th className="px-6 py-2">Store</th>
                                <th className="px-6 py-2 text-right">Remaining Qty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.values(byProduct).flatMap((group) =>
                                group.stores.map((s, i) => (
                                    <tr key={`${group.product?._id}-${s.store?._id}`} className="border-b last:border-0">
                                        <td className="px-6 py-2.5">{i === 0 ? group.product?.name : ''}</td>
                                        <td className="px-6 py-2.5">{s.store?.name}</td>
                                        <td className="px-6 py-2.5 text-right font-medium">{s.quantity}</td>
                                    </tr>
                                ))
                            )}
                            {allocations.length === 0 && (
                                <tr><td colSpan={3} className="px-6 py-6 text-center text-gray-400">No products allocated yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <h3 className="font-medium text-lg p-6 pb-0">Your Sales History</h3>
                    <table className="w-full text-sm mt-3">
                        <thead>
                            <tr className="text-left text-gray-500 text-xs uppercase border-b">
                                <th className="px-6 py-2">Date</th>
                                <th className="px-6 py-2">Store</th>
                                <th className="px-6 py-2">Product</th>
                                <th className="px-6 py-2 text-right">Qty Sold</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sales.map((s) => (
                                <tr key={s._id} className="border-b last:border-0">
                                    <td className="px-6 py-2.5">{new Date(s.soldAt).toLocaleDateString('en-HK')}</td>
                                    <td className="px-6 py-2.5">{s.store?.name}</td>
                                    <td className="px-6 py-2.5">{s.product?.name}</td>
                                    <td className="px-6 py-2.5 text-right font-medium">{s.quantitySold}</td>
                                </tr>
                            ))}
                            {sales.length === 0 && (
                                <tr><td colSpan={4} className="px-6 py-6 text-center text-gray-400">No sales yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

            </div>
        </div>
    );
};

export default VendorDashboard;